// PRD.276 — Elia AI Doctor Consult Integration.
//
// Real chat client: calls the authenticated `peliaDoctorConsult` Firebase
// function. Lives OUTSIDE React (same pattern as useClinicalExtraction /
// useVoiceToText). Never calls IntelisHealth directly. Backend errors are
// translated into a thrown Error so the provider shows the friendly failure
// state and offers Retry.

import type { EliaChatClient } from './elia-chat-types';

export type FirebaseEliaChatClientDeps = {
  /** Injectable for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Injectable for tests. Defaults to the Firebase ID token getter. */
  getToken?: () => Promise<string | null>;
};

async function defaultGetToken(): Promise<string | null> {
  // Dynamic import keeps the Firebase auth dependency out of unit tests, which
  // inject `getToken` and never hit this path.
  const mod = await import('@helios/auth-firebase-web');
  return mod.getIdToken();
}

export function createFirebaseEliaChatClient(
  endpointUrl: string,
  deps: FirebaseEliaChatClientDeps = {},
): EliaChatClient {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const getToken = deps.getToken ?? defaultGetToken;

  return {
    async sendMessage({ query, history, context, signal }) {
      const token = await getToken();
      const hasContext = !!(context && (context.patientId || context.visitId));

      const res = await fetchImpl(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query,
          history,
          ...(hasContext ? { context } : {}),
        }),
        signal,
      });

      if (!res.ok) {
        // Read the safe error code if present; never surface raw upstream text.
        let code = `HTTP_${res.status}`;
        try {
          const body = (await res.json()) as { errorCode?: unknown };
          if (typeof body.errorCode === 'string') code = body.errorCode;
        } catch {
          /* ignore non-JSON error bodies */
        }
        throw new Error(`elia_backend_error:${code}`);
      }

      const body = (await res.json()) as {
        response?: unknown;
        suggestions?: unknown;
        informationUsed?: unknown;
      };
      const response = typeof body.response === 'string' ? body.response : '';
      if (!response.trim()) {
        throw new Error('elia_backend_error:EMPTY_RESPONSE');
      }

      return {
        response,
        ...(Array.isArray(body.suggestions)
          ? { suggestions: body.suggestions.filter((s): s is string => typeof s === 'string') }
          : {}),
        ...(Array.isArray(body.informationUsed)
          ? {
              informationUsed: body.informationUsed.filter(
                (s): s is string => typeof s === 'string',
              ),
            }
          : {}),
      };
    },
  };
}
